import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

export type GuardrailKind = 'entities' | 'categories' | 'threshold' | 'text' | 'llm' | 'url-list' | 'none';
export type GuardrailType = 'Hybrid' | 'API' | 'LLM' | 'RegEx';
export type GuardrailIcon = 'mask' | 'moderation' | 'jailbreak' | 'topic' | 'custom';
export type ThresholdLevel = 'Low' | 'Medium' | 'High';
export type GuardrailStage = 'input' | 'output';

export interface GuardrailOption {
  id: string;
  label: string;
  example?: string;
  checked: boolean;
}

export interface GuardrailOptionGroup {
  id: string;
  label: string;
  options: GuardrailOption[];
}

export interface GuardrailDefinition {
  id: string;
  name: string;
  /** Canonical guardrail name expected by the backend (openai-guardrails-python), e.g. "Contains PII". */
  apiName: string;
  /** Whether this guardrail inspects the incoming prompt or the model's response. */
  stage: GuardrailStage;
  type: GuardrailType;
  icon: GuardrailIcon;
  iconBg: string;
  iconColor: string;
  description: string;
  enabled: boolean;
  kind: GuardrailKind;
  groups?: GuardrailOptionGroup[];
  threshold?: ThresholdLevel;
  text?: string;
  model?: string;
  confidenceThreshold?: number;
  includeReasoning?: boolean;
  systemPromptDetails?: string;
  urlAllowList?: string[];
  block?: boolean;
}

interface GuardrailConfigEntry {
  name: string;
  config: Record<string, unknown>;
}

export interface GuardrailsConfigPayload {
  version: number;
  pre_flight: { version: number; guardrails: GuardrailConfigEntry[] };
  input: { version: number; guardrails: GuardrailConfigEntry[] };
  output: { version: number; guardrails: GuardrailConfigEntry[] };
}

export interface GuardrailTestRequest {
  prompt: string;
  guardrails_config: GuardrailsConfigPayload;
}

interface GuardrailApiResult {
  plain_response: string;
  guardrails_response: {
    not_hit: boolean;
    tripwire_triggered: boolean;
    stage: GuardrailStage | null;
    message: string | null;
    response: string | null;
  };
}

export interface GuardrailApiResponse {
  hasResult: boolean;
  // On error the backend returns `result: []` instead of the object shape above.
  result: GuardrailApiResult | unknown[] | null;
  statusCode: number;
  message: string;
  errorCode: string;
}

export interface GuardrailTestResult {
  notHit: boolean;
  withoutGuardrails: string;
  withGuardrails: string;
  blocked: boolean;
  blockedStage: GuardrailStage | null;
  triggeredGuardrails: string[];
  error: boolean;
}

// ⚠️ TEMP: hardcoded config — replace with real API data when the guardrails backend is ready
// NOTE: `stage` (input vs output) and `apiName` follow the openai-guardrails-python conventions
// inferred from the confirmed example (Moderation = input, Contains PII = output). The stage
// assignments for Jailbreak / Off Topic Prompts / Custom Prompt Check are a reasonable guess —
// confirm against the real backend once available.
const SEED_GUARDRAILS: GuardrailDefinition[] = [
  {
    id: 'mask-pii',
    name: 'Mask PII',
    apiName: 'Contains PII',
    stage: 'input',
    type: 'Hybrid',
    icon: 'mask',
    iconBg: '#EEF2FF',
    iconColor: '#6366F1',
    description: 'Detects and masks personally identifiable information before it reaches the model or the user.',
    enabled: true,
    kind: 'entities',
    block: true,
    groups: [
      {
        id: 'identity',
        label: 'Identity & Contact',
        options: [
          { id: 'PERSON', label: 'Full name', example: 'Jane Doe', checked: true },
          { id: 'EMAIL_ADDRESS', label: 'Email address', example: 'jane.doe@email.com', checked: true },
          { id: 'PHONE_NUMBER', label: 'Phone number', example: '+1 202 555 0148', checked: true },
          { id: 'LOCATION', label: 'Physical address', example: '123 Main St, Anytown, CA', checked: false },
          { id: 'DATE_TIME', label: 'Date of birth', example: '1990-04-12', checked: false }
        ]
      },
      {
        id: 'financial',
        label: 'Financial',
        options: [
          { id: 'CREDIT_CARD', label: 'Credit card number', example: '4111 1111 1111 1111', checked: true },
          { id: 'CREDIT_CARD_CVV', label: 'Card security code (CVV/CVC)', example: '123', checked: true },
          { id: 'IBAN_CODE', label: 'IBAN code', example: 'GB82 WEST 1234 5698 7654 32', checked: false },
          { id: 'CRYPTO', label: 'Cryptocurrency wallet address', example: '0x32Be343B94f8...', checked: false }
        ]
      },
      {
        id: 'ids',
        label: 'Government & Technical IDs',
        options: [
          { id: 'US_SSN', label: 'SSN / National ID', example: '123-45-6789', checked: true },
          { id: 'US_PASSPORT', label: 'Passport number', example: 'X1234567', checked: false },
          { id: 'IP_ADDRESS', label: 'IP address', example: '192.168.1.1', checked: false }
        ]
      }
    ]
  },
  {
    id: 'moderation',
    name: 'Moderation',
    apiName: 'Moderation',
    stage: 'input',
    type: 'API',
    icon: 'moderation',
    iconBg: '#F0FDF4',
    iconColor: '#22C55E',
    description: 'Blocks text flagged by content moderation classifiers before it is processed.',
    enabled: true,
    kind: 'categories',
    groups: [
      {
        id: 'flagged',
        label: 'Flagged Categories',
        options: [
          { id: 'sexual/minors', label: 'Sexual / minors', checked: true },
          { id: 'hate/threatening', label: 'Hate / threatening', checked: true },
          { id: 'harassment/threatening', label: 'Harassment / threatening', checked: true },
          { id: 'self-harm/intent', label: 'Self-harm / intent', checked: true },
          { id: 'self-harm/instructions', label: 'Self-harm / instructions', checked: true },
          { id: 'violence/graphic', label: 'Violence / graphic', checked: true },
          { id: 'illicit/violent', label: 'Illicit / violent', checked: true }
        ]
      }
    ]
  },
  {
    id: 'jailbreak',
    name: 'Jailbreak Detection',
    apiName: 'Jailbreak',
    stage: 'input',
    type: 'LLM',
    icon: 'jailbreak',
    iconBg: '#FEF2F2',
    iconColor: '#EF4444',
    description: 'Detects attempts to jailbreak model calls via role-play, system prompt overrides, and prompt injection.',
    enabled: true,
    kind: 'llm',
    model: 'gpt-4.1-mini',
    confidenceThreshold: 0.7,
    includeReasoning: false
  },
  {
    id: 'url-filter',
    name: 'URL Filter',
    apiName: 'URL Filter',
    stage: 'output',
    type: 'RegEx',
    icon: 'custom',
    iconBg: '#FFFBEB',
    iconColor: '#F59E0B',
    description: 'Blocks outputs with URLs not matching an allow list.',
    enabled: true,
    kind: 'url-list',
    urlAllowList: []
  },
  {
    id: 'contains-pii-output',
    name: 'Contains PII',
    apiName: 'Contains PII',
    stage: 'output',
    type: 'Hybrid',
    icon: 'mask',
    iconBg: '#EEF2FF',
    iconColor: '#6366F1',
    description: 'Checks that the text does not contain personally identifiable information before it is returned to the user.',
    enabled: true,
    kind: 'entities',
    block: true,
    groups: [
      {
        id: 'identity',
        label: 'Identity & Contact',
        options: [
          { id: 'PERSON', label: 'Full name', example: 'Jane Doe', checked: true },
          { id: 'EMAIL_ADDRESS', label: 'Email address', example: 'jane.doe@email.com', checked: true },
          { id: 'PHONE_NUMBER', label: 'Phone number', example: '+1 202 555 0148', checked: true },
          { id: 'LOCATION', label: 'Physical address', example: '123 Main St, Anytown, CA', checked: true },
          { id: 'DATE_TIME', label: 'Date of birth', example: '1990-04-12', checked: true },
          { id: 'IP_ADDRESS', label: 'IP address', example: '192.168.1.1', checked: true },
          { id: 'URL', label: 'URL', example: 'https://example.com', checked: true }
        ]
      },
      {
        id: 'financial',
        label: 'Financial',
        options: [
          { id: 'CREDIT_CARD', label: 'Credit card number', example: '4111 1111 1111 1111', checked: true },
          { id: 'CVV', label: 'Card security code (CVV/CVC)', example: '123', checked: true },
          { id: 'CRYPTO', label: 'Cryptocurrency wallet address', example: '0x32Be343B94f8...', checked: true },
          { id: 'IBAN_CODE', label: 'IBAN code', example: 'GB82 WEST 1234 5698 7654 32', checked: true },
          { id: 'BIC_SWIFT', label: 'BIC / SWIFT', example: 'DEUTDEFF', checked: true }
        ]
      },
      {
        id: 'other',
        label: 'Other IDs',
        options: [
          { id: 'MEDICAL_LICENSE', label: 'Medical license', example: 'MD123456', checked: true },
          { id: 'NRP', label: 'National registry ID', example: '1234567890', checked: true }
        ]
      }
    ]
  },
  {
    id: 'nsfw-text',
    name: 'NSFW Text',
    apiName: 'NSFW Text',
    stage: 'output',
    type: 'LLM',
    icon: 'custom',
    iconBg: '#FEF2F2',
    iconColor: '#EF4444',
    description: 'Detects NSFW content in generated text before it is returned.',
    enabled: true,
    kind: 'llm',
    model: 'gpt-4.1-mini',
    confidenceThreshold: 0.7,
    includeReasoning: false
  },
  {
    id: 'custom-prompt',
    name: 'Custom Prompt Check',
    apiName: 'Custom Prompt Check',
    stage: 'input',
    type: 'LLM',
    icon: 'custom',
    iconBg: '#F5F3FF',
    iconColor: '#A855F7',
    description: 'Blocks requests that match a custom moderation criteria prompt you define.',
    enabled: false,
    kind: 'llm',
    model: 'gpt-4.1-mini',
    confidenceThreshold: 0.7,
    includeReasoning: false,
    systemPromptDetails: 'You are a customer support assistant. Raise the guardrail if questions aren’t focused on customer inquiries, product support, and service-related questions.'
  }
];

@Injectable({ providedIn: 'root' })
export class GuardrailConfigService {
  private http = inject(HttpClient);
  guardrails = signal<GuardrailDefinition[]>(clone(SEED_GUARDRAILS));

  getById(id: string): GuardrailDefinition | undefined {
    return this.guardrails().find(g => g.id === id);
  }

  setEnabled(id: string, enabled: boolean) {
    this.guardrails.update(list => list.map(g => (g.id === id ? { ...g, enabled } : g)));
  }

  saveGuardrail(updated: GuardrailDefinition) {
    this.guardrails.update(list => list.map(g => (g.id === updated.id ? clone(updated) : g)));
  }

  selectedCount(guardrail: GuardrailDefinition): { checked: number; total: number } {
    const options = (guardrail.groups ?? []).flatMap(g => g.options);
    return { checked: options.filter(o => o.checked).length, total: options.length };
  }

  buildGuardrailsConfigPayload(): GuardrailsConfigPayload {
    const enabled = this.guardrails().filter(g => g.enabled);
    const toEntry = (g: GuardrailDefinition): GuardrailConfigEntry => ({
      name: g.apiName,
      config: this.buildGuardrailConfig(g)
    });

    const PRE_FLIGHT_IDS = new Set(['mask-pii', 'moderation']);

    return {
      version: 1,
      pre_flight: { version: 1, guardrails: enabled.filter(g => g.stage === 'input' && PRE_FLIGHT_IDS.has(g.id)).map(toEntry) },
      input:      { version: 1, guardrails: enabled.filter(g => g.stage === 'input' && !PRE_FLIGHT_IDS.has(g.id)).map(toEntry) },
      output:     { version: 1, guardrails: enabled.filter(g => g.stage === 'output').map(toEntry) }
    };
  }

  private buildGuardrailConfig(g: GuardrailDefinition): Record<string, unknown> {
    switch (g.kind) {
          case 'entities': {
        const piiEntities = [
          'CREDIT_CARD',
          'CVV',
          'CRYPTO',
          'DATE_TIME',
          'EMAIL_ADDRESS',
          'IBAN_CODE',
          'BIC_SWIFT',
          'IP_ADDRESS',
          'LOCATION',
          'MEDICAL_LICENSE',
          'NRP',
          'PERSON',
          'PHONE_NUMBER',
          'URL'
        ];

        const entities = g.id === 'contains-pii-output' || g.id === 'mask-pii'
          ? piiEntities
          : (g.groups ?? []).flatMap(group => group.options.filter(o => o.checked).map(o => o.id));

        const config: Record<string, unknown> = { entities };
        if (g.stage === 'output') {
          config['block'] = true;
        }
        return config;
      }
      case 'categories':
        return { categories: (g.groups ?? []).flatMap(group => group.options.filter(o => o.checked).map(o => o.id)) };
      case 'threshold':
        return { sensitivity: g.threshold ?? 'Medium' };
      case 'llm': {
        const config: Record<string, unknown> = {
          confidence_threshold: g.confidenceThreshold ?? 0.7,
          model: g.model ?? 'gpt-4.1-mini',
          include_reasoning: g.includeReasoning ?? false
        };
        if (g.systemPromptDetails) {
          config['system_prompt_details'] = g.systemPromptDetails;
        }
        if (g.text) {
          config['system_prompt_details'] = g.text;
        }
        return config;
      }
      case 'url-list':
        return { url_allow_list: g.urlAllowList ?? [] };
      case 'text':
        return { prompt: g.text ?? '' };
      case 'none':
        return {};
    }
  }

  async saveConfiguration(): Promise<void> {
    const configJson = JSON.stringify(this.buildGuardrailsConfigPayload());
    await firstValueFrom(
      this.http.post(`${environment.apiUrl}/guardrail-configuration`, { configJson })
    );
  }

  runTest(prompt: string): Observable<GuardrailTestResult> {
    const request: GuardrailTestRequest = { prompt, guardrails_config: this.buildGuardrailsConfigPayload() };
    const baseUrl = environment.guardrailsBaseUrl || environment.apiUrl;
    const url = baseUrl.replace(/\/$/, '') + '/api/llm-guardrails/check';

    return this.http.post<GuardrailApiResponse>(url, request).pipe(
      map(res => this.mapApiResponse(res)),
      catchError(err => of(this.mapErrorResponse(err)))
    );
  }

  private mapApiResponse(api: GuardrailApiResponse): GuardrailTestResult {
    const result = !Array.isArray(api.result) ? api.result : null;

    if (!api.hasResult || !result) {
      const message = api.message || 'The guardrails API returned an unexpected response.';
      return { notHit: false, withoutGuardrails: message, withGuardrails: message, blocked: true, blockedStage: null, triggeredGuardrails: [], error: true };
    }

    const gr = result.guardrails_response;
    return {
      notHit: gr.not_hit ?? false,
      withoutGuardrails: result.plain_response,
      withGuardrails: gr.tripwire_triggered ? gr.message ?? 'Blocked by guardrails.' : gr.response ?? result.plain_response,
      blocked: gr.tripwire_triggered,
      blockedStage: gr.stage,
      triggeredGuardrails: [],
      error: false
    };
  }

  private mapErrorResponse(err: unknown): GuardrailTestResult {
    const message = err instanceof HttpErrorResponse
      ? (err.error?.message || err.message || 'Request to the guardrails API failed.')
      : 'Request to the guardrails API failed.';
    return { notHit: false, withoutGuardrails: message, withGuardrails: message, blocked: true, blockedStage: null, triggeredGuardrails: [], error: true };
  }

}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}
